import { Badge, Card, Text, Title } from '@tremor/react';
import type { SupportBoardTicket, SupportTicketState } from '../../api/supportTickets';
import { t } from '../../lib/i18n';
import { formatRelativeDay } from '../../lib/format';

export const COLUMNS: SupportTicketState[] = ['received', 'in_progress', 'done', 'closed'];

/**
 * Buckets tickets by state for the four-column board, preserving each
 * ticket's position within its column (the server already orders the list;
 * we don't second-guess it). A ticket whose state the SPA doesn't recognize
 * (a licensing-server rollout ahead of a stale SPA build, or a malformed
 * fallback payload) is silently dropped rather than crashing the board —
 * `in` guards the lookup instead of indexing straight into `out`.
 */
export function groupByState(tickets: SupportBoardTicket[]): Record<SupportTicketState, SupportBoardTicket[]> {
  const out: Record<SupportTicketState, SupportBoardTicket[]> = {
    received: [], in_progress: [], done: [], closed: [],
  };
  for (const ticket of tickets) {
    if (ticket.state in out) out[ticket.state].push(ticket);
  }
  return out;
}

/** Shared with TicketDetailPanel's kind badge so the two surfaces never drift. */
export function kindColor(kind: string): 'red' | 'blue' | 'amber' | 'gray' {
  switch (kind) {
    case 'bug':        return 'red';
    case 'suggestion': return 'blue';
    case 'question':   return 'amber';
    case 'report':     return 'gray';
    default:           return 'gray';
  }
}

interface Props {
  tickets: SupportBoardTicket[];
  onOpen: (ticket: SupportBoardTicket) => void;
}

export default function TicketBoard({ tickets, onOpen }: Props) {
  const g = groupByState(tickets);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {COLUMNS.map((col) => (
        <div key={col} className="space-y-2">
          <Title className="text-sm">
            {t.supportTickets.columns[col]} <span className="text-tremor-content">({g[col].length})</span>
          </Title>
          {g[col].map((ticket) => (
            <Card
              key={ticket.identifier}
              onClick={() => onOpen(ticket)}
              className="cursor-pointer p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-xs text-tremor-content">{ticket.identifier}</span>
                {ticket.unread > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-tremor-brand text-tremor-brand-inverted text-xs"
                    aria-label={`${ticket.unread} ${t.supportTickets.unreadLabel}`}
                  >
                    {ticket.unread}
                  </span>
                )}
              </div>
              <Text className="text-sm font-medium mt-0.5">{ticket.title}</Text>
              <div className="mt-1">
                <Badge color={kindColor(ticket.kind)}>{t.supportTickets.kind[ticket.kind]}</Badge>
              </div>
              <div className="flex justify-between items-baseline mt-1.5">
                <Text className="text-xs">{ticket.reporter}</Text>
                <Text className="text-xs text-tremor-content">{formatRelativeDay(ticket.updated_at)}</Text>
              </div>
            </Card>
          ))}
        </div>
      ))}
    </div>
  );
}
